// src/pages/customer/image/imageEntryForm.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Avatar,
  LinearProgress,
  Alert,
} from "@mui/material";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useSelectedBusiness } from "../../../redux/store";
import { useMutation } from "@apollo/client";
import {
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogContentText,
  StyledDialogActions,
} from "../../../assets/css/styledComponent";
import { uploadToGCS } from "../../../util/upload/uploadToGCS";
import {
  DELETE_IMAGE,
  GENERATE_UPLOAD_URL,
  UPDATE_IMAGE,
} from "../../../graphql/mutations/image";
import { uploadToCloudinary } from "../../../util/upload/uploadToCloudinary";
import { BusinessQueryNames } from "../../../graphql/queries";
import useLoadReduxStoreData from "../../../redux/reducers/loadReduxStoreData";
import { useAuthStatus } from "../../../util/useAuthStatus";

// ✅ import your existing dialog component
import UpgradePlan from "../../../util/upgrade/upgradePlan";

/**
 * ImageEntryForm with plan-based image limits.
 *
 * Props:
 * - singleImageMode: if true, force 1 image regardless of plan
 * - avatar: use avatar layout/sizes (1x1)
 * - staffId: for staff profile saves
 * - isDisabled: disable all inputs
 * - effectivePlan: number (e.g., 1, 2, 3) from getEffectivePlan(providerUser)
 * - requiredPlanId: minimum plan that unlocks multi-image (default 2)
 * - planImageLimits: map of planId -> max images, e.g., {2: 3, 3: 5}
 */
const ImageEntryForm = ({
  singleImageMode = true,
  avatar = false,
  staffId = null,
  residentId = null,
  residentImageUrl = "",
  isDisabled = false,
  effectivePlan = 0,
  requiredPlanId = 2,
  planImageLimits = { 2: 5, 3: 10 },
}) => {
  const [images, setImages] = useState([]);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [openConfirmStyledDialog, setOpenConfirmStyledDialog] = useState(false);
  const [checkPlanNeeded, setCheckPlanNeeded] = useState(false); // 👈 opens dialog only when you set it true
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState("");

  const selectedBusiness = useSelectedBusiness();
  const [generateUploadUrl] = useMutation(GENERATE_UPLOAD_URL);
  const [updateImage] = useMutation(UPDATE_IMAGE);
  const [deleteImage] = useMutation(DELETE_IMAGE);
  const { isLoggedIn } = useAuthStatus();

  const { refetch: reloadBusinesses } = useLoadReduxStoreData(
    BusinessQueryNames.GET_MY_BUSINESSES,
    { enabled: isLoggedIn }
  );

  // ---- PLAN LIMITS ----------------------------------------------------------
  const maxAllowed = useMemo(() => {
    if (singleImageMode) return 1;
    if (effectivePlan < requiredPlanId) return 1;
    const configured = planImageLimits[effectivePlan];
    if (typeof configured === "number") return configured;
    const vals = Object.values(planImageLimits || {});
    return vals.length ? Math.max(...vals) : 1;
  }, [singleImageMode, effectivePlan, requiredPlanId, planImageLimits]);

  const used = images.length;
  const canAddMore = used < maxAllowed;

  // Hidden file input (we open it only if allowed)
  const fileRef = useRef(null);

const isSingleMode = singleImageMode;
  const targetLabel = residentId
    ? "resident photo"
    : staffId
      ? "staff photo"
      : "business image";
  const isValidId = (value) => value !== undefined && value !== null && `${value}`.trim() !== "";

  const getCurrentImages = useCallback((sourceBusiness) => {
    if (!sourceBusiness) return [];

    if (residentId) {
      const match = sourceBusiness?.residents?.find(
        (r) => Number(r.id) === Number(residentId)
      );
      const residentUrl = match?.image?.url || match?.photoUrl || residentImageUrl;
      const residentImageId = match?.image?.id;

      return residentUrl
        ? [{ id: residentImageId, url: residentUrl, name: "Resident Photo" }]
        : [];
    }

    if (avatar) {
      const currentStaff = sourceBusiness?.businessStaffs?.find(
        (staff) => Number(staff.id) === Number(staffId)
      );

      return currentStaff?.image?.url
        ? [{ id: currentStaff.image.id, url: currentStaff.image.url, name: "Staff Photo" }]
        : [];
    }

    return (
      sourceBusiness?.businessBasic?.images
        ?.filter((item) => item.businessId !== null)
        .map((item) => ({
          id: item.id,
          url: item.url,
          name: `picture ${item.id}`,
        })) || []
    );
  }, [avatar, residentId, residentImageUrl, staffId]);

 const tryAddImage = () => {
  if (fileRef.current) {
    // Ensure selecting the same file still triggers onChange.
    fileRef.current.value = "";
  }
  if (isSingleMode) {
    // Always allow replace in single image mode
    fileRef.current?.click();
    return;
  }

  if (canAddMore) {
    fileRef.current?.click();
  } else {
    setCheckPlanNeeded(true);
  }
};

  // ---- LOAD EXISTING IMAGES -------------------------------------------------
  // ---- LOAD EXISTING IMAGES -------------------------------------------------
  useEffect(() => {
    setImages(getCurrentImages(selectedBusiness));
  }, [getCurrentImages, selectedBusiness]);


  // ---- DND PARTS ------------------------------------------------------------
  const DraggableImage = ({ index }) => {
    const [{ isDragging }, drag] = useDrag({
      type: "IMAGE",
      item: { index },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    const [showDelete, setShowDelete] = useState(false);

    const handleClick = () => setShowDelete((x) => !x);

    const askDelete = () => {
      setDeleteIndex(index);
      setOpenConfirmStyledDialog(true);
    };

    return (
      <div
        ref={drag}
        style={{
          opacity: isDragging ? 0.5 : 1,
          cursor: "move",
          position: "relative",
        }}
      >
        <Paper
          style={{ position: "relative" }}
          onClick={handleClick}
          onMouseEnter={() => setShowDelete(true)}
          onMouseLeave={() => setShowDelete(false)}
        >
          {avatar ? (
            <Avatar src={images[index].url} alt={`Image ${index}`} sx={{ width: 56, height: 56 }} />
          ) : (
            <img src={images[index].url} alt={`Media ${index + 1}`} style={{ width: "100%" }} />
          )}
          <Typography variant="caption">{images[index].name}</Typography>
          {showDelete && (
            <Button onClick={askDelete} size="small" color="error">
              Delete
            </Button>
          )}
        </Paper>
      </div>
    );
  };

  const DropZone = ({ index, children }) => {
    const [, drop] = useDrop({
      accept: "IMAGE",
      drop: (item) => {
        const dragIndex = item.index;
        const dropIndex = index;
        if (dragIndex === dropIndex) return;
        const updatedImages = [...images];
        const [removed] = updatedImages.splice(dragIndex, 1);
        updatedImages.splice(dropIndex, 0, removed);
        setImages(updatedImages);
      },
    });

    return <div ref={drop}>{children}</div>;
  };

  // ---- ADD / DELETE ---------------------------------------------------------
  const handleAddImage = async (e) => {
    setActionError("");
    const files = e.target.files;
    if (!files || !files.length) return;

    // In single-image mode, selecting a new file is a replace operation, not an add.
    const baseCount = singleImageMode ? 0 : images.length;
    const wouldBe = baseCount + files.length;
    if (wouldBe > maxAllowed) {
      setCheckPlanNeeded(true);
      e.target.value = "";
      return;
    }

    if (!selectedBusiness && !residentId && !staffId) {
      console.error("No valid target model for image upload");
      setActionError("Unable to update image right now. Please refresh and try again.");
      return;
    }

    try {
      setBusy(true);

      const file = files[0];

      const uploadedUrl =
        process.env.REACT_APP_ENV === "development"
          ? await uploadToCloudinary(file)
          : await uploadToGCS(file, generateUploadUrl);

      // Determine MODEL
      let updateModel = "business";
      if (staffId) updateModel = "staff";
      if (residentId) updateModel = "resident";

      const targetId = residentId || staffId || selectedBusiness?.businessBasic?.id;
      if (!isValidId(targetId)) {
        console.error(`Unable to update ${targetLabel}: missing target id`);
        setActionError("Missing profile id for image update.");
        return;
      }

      const updateResult = await updateImage({
        variables: {
          id: String(targetId),
          url: `${updateModel},${uploadedUrl}`,
        },
      });
      if (!updateResult?.data?.updateImage?.url) {
        throw new Error("Image update mutation returned no image URL.");
      }

      // Local preview
      const newImages = Array.from(files).map((file) => ({
        url: uploadedUrl,
        name: file.name.split(".").slice(0, -1).join("."),
      }));

      setImages(singleImageMode ? [newImages[0]] : [...images, ...newImages]);

      // Refetch and hydrate image ids from backend so delete mutation always has a valid ID.
      await reloadBusinesses();
      setActionError("");

    } catch (err) {
      console.error("Upload failed:", err);
      setActionError("Image update failed. Please try a different file.");
    } finally {
      if (e?.target) e.target.value = "";
      setBusy(false);
    }
  };



  const handleDeleteImage = async () => {
    if (deleteIndex === null) return;

    const imageToDelete = images[deleteIndex];
    const fallbackImageId = (() => {
      if (residentId) {
        const resident = selectedBusiness?.residents?.find(
          (r) => Number(r.id) === Number(residentId)
        );
        return resident?.image?.id;
      }
      if (staffId) {
        const staff = selectedBusiness?.businessStaffs?.find(
          (s) => Number(s.id) === Number(staffId)
        );
        return staff?.image?.id;
      }
      return undefined;
    })();
    const businessId = selectedBusiness?.businessBasic?.id;
    const deleteIdCandidates = [
      imageToDelete?.id,
      fallbackImageId,
      residentId,
      staffId,
      businessId,
    ];
    const deleteId = deleteIdCandidates.find((candidate) => isValidId(candidate));
    if (!isValidId(deleteId)) {
      console.error(`Unable to delete ${targetLabel}: missing image id`);
      setOpenConfirmStyledDialog(false);
      setDeleteIndex(null);
      return;
    }
    const bucketUrl = `https://storage.googleapis.com/${process.env.REACT_APP_GCLOUD_BUCKET_NAME}/`;
    const filename = imageToDelete.url.startsWith(bucketUrl)
      ? imageToDelete.url.replace(bucketUrl, "")
      : imageToDelete.url;

    try {
      const { data } = await deleteImage({ variables: { filename, id: String(deleteId) } });
      if (data.deleteImage) {
        const updated = images.filter((_, idx) => idx !== deleteIndex);
        setImages(updated);
        setOpenConfirmStyledDialog(false);
        setDeleteIndex(null);
        setActionError("");
      } else {
        console.error("Deletion failed: Mutation returned false");
      }
    } catch (error) {
      console.error("Deletion error:", error);
      setActionError("Image delete failed. Please try again.");
    }
  };

  const cancelDelete = () => {
    setOpenConfirmStyledDialog(false);
    setDeleteIndex(null);
  };

  // ---- RENDER ---------------------------------------------------------------
  const uploadDisabled = isDisabled || busy;

  return (
    <Container>
      {busy && <LinearProgress />}
      {!!actionError && (
        <Alert sx={{ mt: 1.5 }} severity="error">
          {actionError}
        </Alert>
      )}

      <Box mt={4} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
        <Typography variant="h4" gutterBottom>
          Photo
        </Typography>
        <Typography variant="body2" color={!canAddMore ? "error.main" : "text.secondary"}>
          {used} / {maxAllowed} {maxAllowed > 1 ? "images" : "image"}
        </Typography>
      </Box>

      {/* Hidden file input */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={handleAddImage}
        style={{ display: "none" }}
        multiple={!singleImageMode}
      />

      {/* Upload / Upgrade button with a single gate */}
      <Box mt={2} display="flex" gap={1} alignItems="center">
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            width: "100%",
            borderStyle: "dashed",
            borderColor: (t) => t.palette.divider,
            borderRadius: 2,
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {isSingleMode ? "Upload profile image" : "Upload images"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isSingleMode
                  ? `Use a clear square photo for the ${residentId ? "resident" : staffId ? "staff" : "business"} profile.`
                  : `You can upload up to ${maxAllowed} images.`}
              </Typography>
            </Box>
            <Button
              disabled={uploadDisabled}
              variant="contained"
              onClick={tryAddImage}
            >
              {isSingleMode
                ? images.length > 0
                  ? "Update Image"
                  : "Upload Image"
                : canAddMore
                  ? "Upload Image"
                  : "Upgrade for more"}
            </Button>
          </Box>
        </Paper>
      </Box>
      {isDisabled && (
        <Alert sx={{ mt: 1.5 }} severity="warning">
          Image editing is disabled for this account.
        </Alert>
      )}
      {isSingleMode && images.length === 0 && (
        <Alert sx={{ mt: 1.5 }} severity="info">
          No image uploaded yet.
        </Alert>
      )}

      {/* Grid of images (drag to reorder) */}
      <Box mt={4}>
        <DndProvider backend={HTML5Backend}>
          <Grid container spacing={2}>
            {images.map((_, index) => (
              <Grid key={index} item xs={6} sm={4} md={3} lg={3} xl={2}>
                <DropZone index={index}>
                  <DraggableImage index={index} />
                </DropZone>
              </Grid>
            ))}
          </Grid>
        </DndProvider>
      </Box>

      {/* Delete Confirmation */}
      <StyledDialog open={openConfirmStyledDialog} onClose={cancelDelete}>
        <StyledDialogTitle>Delete Image?</StyledDialogTitle>
        <StyledDialogContent>
          <StyledDialogContentText>
            This will permanently remove "{deleteIndex !== null && images[deleteIndex]?.name}" from this profile.
          </StyledDialogContentText>
        </StyledDialogContent>
        <StyledDialogActions>
          <Button onClick={cancelDelete}>Cancel</Button>
          <Button onClick={handleDeleteImage} color="error" variant="contained">
            Delete
          </Button>
        </StyledDialogActions>
      </StyledDialog>

      {/* Upgrade Modal — renders ONLY when you click or exceed cap on selection */}
      {checkPlanNeeded && (
        <UpgradePlan
          featureName={"Add more photos"}
          requiredPlanId={requiredPlanId}
          onClose={() => setCheckPlanNeeded(false)}
        />
      )}
    </Container>
  );
};

export default ImageEntryForm;
